<template lang="pug">
.checkout.neutral300
  template(v-if="isFetchingInvoice || isFetchingAvailableSeats || isInit")
    v-container.pa-4(fluid)
      v-row(no-gutters)
        v-col
          v-row
            v-col(cols="1")
              v-card.round-xl(outlined link width="36" height="36" @click="onClickBack")
                div.d-flex.align-center.justify-center.fill-height
                  v-icon(color="primary") mdi-arrow-left
            v-col
              v-skeleton-loader(type="article@5")
        v-col
          v-skeleton-loader(type="article@5")
  template(v-else)
    //- header
    v-row.px-6.py-3.neutral100(no-gutters)
      v-col(cols="4")
        div.d-flex.align-center
          v-card.round-xl(outlined link @click="onClickBack" width="36" height="36")
            div.d-flex.align-center.justify-center.fill-height
              v-icon(color="primary") mdi-arrow-left
          div.text-xl.font-semibold.neutral700--text.ml-4 Invoice {{ invoice.rid ? `#${invoice.rid}` : '' }}
          y-button.ml-3(v-if="canOpenConsultation" label="View consultation" size="xxs" outlined color="neutral400" text-color="neutral700" unset-min-width button-class="px-2" @click="openConsultationNotes")
          y-button.ml-3(label="View activity log" size="xxs" outlined color="neutral400" text-color="neutral700" unset-min-width button-class="px-2" @click="showActivityLogDrawer = true")

      v-col(cols="8")
        div.d-flex.align-center.fill-height
          v-spacer
          template(v-if="registration")
            v-card.round-xl.pl-3.pr-1(v-if="registration.checkout_note" outlined height="36" style="background-color: var(--v-yellow100);")
              div.d-flex.align-center.fill-height
                div.text-sm.font-medium.neutral700--text.ellipsis-text-1 Dispense note:
                  span.font-normal.ml-1 {{ registration.checkout_note }}
                y-button.ml-3(label="Read more" outlined color="neutral400" text-color="neutral700" background-color="var(--v-neutral100)" size="xxs" unset-min-width button-class="px-2" @click="showDispenseNoteDialog = true")
            v-card.round-xl.pl-3.pr-1.ml-4(v-if="registration.status !== 'completed' && registration.checkout_at" outlined height="36" style="background-color: var(--v-neutral300);")
              div.d-flex.align-center.fill-height
                div.text-sm.font-medium.neutral700--text.no-wrap {{ `Waiting: ${getWaitingDuration(registration)}` }}
                v-menu(v-if="!isCompleted" offset-y rounded="lg")
                  template(v-slot:activator="{ on }")
                    div(v-on="on")
                      y-button.ml-3(label="Call in" size="xxs" unset-min-width button-class="px-2" :loading="isLoadingCallPatientIn" @click="")
                  v-list.py-0
                    template(v-if="seats.length > 0")
                      template(v-for="(seat, seatIdx) in seats")
                        v-divider(v-if="seats !== 0")
                        v-list-item(@click="onCallPatientIn(seat)" :key="'action-' + seatIdx")
                          v-list-item-title.text-sm.basic800--text {{ seat.name }}
                    template(v-else)
                      v-list-item
                        v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
          y-button.ml-4(v-if="showSwitchOldVersion" label="Switch old version" outlined color="neutral300" text-color="neutral700" background-color="var(--v-neutral300)" append-icon="$yzi-repeat-01" icon-size="15" icon-color="neutral700" size="xxs" height="24" @click="showAlert = true")

    v-divider
    //- body
    v-row.pa-4(no-gutters)
      //- left panel
      v-col.pr-2(cols="4")
        div.parent-scroll(:style="{ '--page-height': pageHeight }")
          div.child-scroll(:style="{ '--page-height': pageHeight, '--nve-right-margin': nveRightMargin }")
            left-panel-checkout
      //- middle panel
      v-col.px-2(cols="5")
        middle-panel-checkout
      //- right panel
      v-col.pl-2(cols="3")
        div.parent-scroll(:style="{ '--page-height': pageHeight }")
          div.child-scroll(:style="{ '--page-height': pageHeight, '--nve-right-margin': nveRightMargin }")
            right-panel-checkout(@completed="onClickBack")

  .dialogs
    dispense-note-dialog(v-if="showDispenseNoteDialog" :open.sync="showDispenseNoteDialog" :note="registration.checkout_note")
    activity-log-drawer(v-if="showActivityLogDrawer" :open.sync="showActivityLogDrawer" :invoice-pid="leadPid")
    y-dialog(v-if="showAlert" v-model="showAlert" :max-width="600" persistent content-class="round-2xl")
      v-card
        v-card-title.pa-4
          v-row(no-gutters align="center")
            v-col(cols="10" offset="1")
              div.text-lg.font-semibold.text-center.neutral700--text Switch back
            v-col(cols="1" align="right")
              v-btn(icon @click="showAlert = false" small color="primary")
                v-icon mdi-close
        v-divider
        v-card-text.pa-6
          div.text-md.font-semibold.neutral700--text You’re about to switch into old version of Dispensary page. The old version will be retired in a few months.
          div.text-sm.neutral700--text.mt-4 Click “Confirm” to continue
        v-divider
        v-card-actions.pb-4.pt-3.px-6
          v-spacer
          y-button.mr-3(:label="$t('cancel')" text color="neutral700" size="sm" button-class="px-0" @click="showAlert = false")
          y-button(label="Confirm" color="neutral700" size="sm" button-class="px-3" dark @click="switchOldVersion")

</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import LeftPanelCheckout from '@/components/clinic/checkout/LeftPanelCheckout.vue'
import MiddlePanelCheckout from '@/components/clinic/checkout/MiddlePanelCheckout.vue'
import RightPanelCheckout from '@/components/clinic/checkout/RightPanelCheckout.vue'
import DispenseNoteDialog from '@/components/clinic/checkout/DispenseNoteDialog.vue'
import ActivityLogDrawer from '@/components/clinic/checkout/ActivityLogDrawer.vue'

export default {
  components: { LeftPanelCheckout, MiddlePanelCheckout, RightPanelCheckout, DispenseNoteDialog, ActivityLogDrawer },
  data () {
    return {
      isFetchingInvoice: false,
      isFetchingAvailableSeats: false,
      isLoadingCallPatientIn: false,
      isInit: true,
      isNavigatingToConsultation: false,
      showDispenseNoteDialog: false,
      showActivityLogDrawer: false,
      showAlert: false,
      timer: null,
      currentTime: null,
      seats: []
    }
  },
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapState('clinic', ['cmsOsV2Enabled']),
    showSwitchOldVersion () {
      if (this.cmsOsV2Enabled) return false
      // Hide for members created strictly after 25 Jun 2026 (i.e. 26 Jun onward)
      const created = this.member && this.member.created
      if (created && DateTime.fromISO(created) >= DateTime.fromISO('2026-06-26')) return false
      return true
    },
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      invoice: 'treatment/getInvoice',
      registration: 'treatment/getRegistration',
      displaySize: 'ui/getDisplaySize',
      scrollbarWidth: 'ui/getScrollbarWidth',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    screenHeight () {
      return window.innerHeight
    },
    pageHeight () {
      let minusHeight = 168
      switch (this.displaySize) {
        case 'xx-small':
          return `${(this.screenHeight / 0.67) - minusHeight}px`
        case 'x-small':
          return `${(this.screenHeight / 0.75) - minusHeight}px`
        case 'small':
          return `${(this.screenHeight / 0.8) - minusHeight}px`
        case 'medium':
          return `${(this.screenHeight / 0.9) - minusHeight}px`
        case 'large':
          return `${(this.screenHeight) - minusHeight}px`
        case 'x-large':
          return `${(this.screenHeight / 1.1) - minusHeight}px`
        default:
          return `${(this.screenHeight) - minusHeight}px`
      }
    },
    nveRightMargin () {
      return `${(-10 - this.scrollbarWidth)}px`
    },
    isCompleted() {
      // For migrated data, always completed
      if (!this.registration) return true
      return this.registration.status === 'completed'
    },
    canOpenConsultation () {
      // if (!this.Auth('page', '/admin/clinic/consultation')) {
      //   return false
      // }
      if (this.isHq) {
        return false
      }
      if (this.registration?.code !== 3) {
        return true
      }
      return false
    },
  },
  created () {
    this.setData()
  },
  mounted () {
    this.fetchData()
    this.isInit = false
  },
  beforeDestroy () {
    let pid = this.leadPid
    this.clearTimer()
    this.$store.commit('treatment/resetTreatment')
    if (this.isNavigatingToConsultation) {
      this.$store.commit('treatment/setLeadPid', pid)
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.updateTime()
      this.timer = setInterval(this.updateTime, 1000);
      this.$store.commit('treatment/setLeadPid', this.$route.params.pid)
    },
    fetchData () {
      this.fetchInvoice(true)
      this.fetchAvailableSeats()
    },
    fetchInvoice(showFetching = false) {
      if (showFetching) {
        this.isFetchingInvoice = true
      }
      const endpoint = this.isHq ? `/hq/invoices/${this.leadPid}` : `/invoices/${this.leadPid}`
      this.$axios.get(endpoint)
        .then(resp => {
          this.$store.commit('treatment/setInvoice', resp.data)
          if (this.isHq) {
            this.$store.commit('hq/setSelectedCompany', resp.data.company)
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingInvoice = false
        })
    },
    fetchAvailableSeats () {
      this.isFetchingAvailableSeats = true
      let params = {
        status: 'active',
        type: 'room',
        fields: 'id,name',
        page_size: -1
      }
      return this.$axios.get(`/clinics/${this.company.slug}/facilities`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.seats = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingAvailableSeats = false
        })
    },
    async onCallPatientIn(seat) {
      this.isLoadingCallPatientIn = true
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { seat: seat?.id })
        await this.fetchInvoice()
      } catch (error) {
        const errorData = error.response?.data?.detail
        if (errorData && errorData.includes('currently occupied')) {
          this.showMessage(['error', `${seat?.value} is currently occupied`])
          return
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingCallPatientIn = false
      }
    },
    updateTime() {
      this.currentTime = new Date().toISOString()
    },
    getWaitingDuration(item) {
      const end = DateTime.fromISO(this.currentTime)
      const start = DateTime.fromISO(item.checkout_at)

      let diff = end.diff(start, ['hours', 'minutes']);
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      if (hours) {
        return `${hours} ${this.$options.filters.pluralize('hour', { count:hours })}  ${minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
      }
      return `${minutes < 1 ? '<1' : minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
    },
    onClickBack () {
      this.showAlert = false
      let route = { name: this.isHq ? 'HQPatientList' : 'ClinicRegistration' }
      if (this.$route.params && this.$route.params.origin) {
        route = { name: this.$route.params.origin }
      }
      if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
        route = { ...route, ...this.$route.params.data }
      }
      return this.$router.push(route).catch(() => {})
    },
    openConsultationNotes () {
      this.isNavigatingToConsultation = true
      let next = {
        name: 'ClinicConsultationDetails',
        params: {
          pid: this.leadPid,
          origin: 'ClinicCheckout',
          data: { params: { pid: this.leadPid, origin: this.$route.params.origin, data: this.$route.params.data } }
        }
      }
      this.$router.push(next)
    },
    switchOldVersion () {
      let params = {
        extra_data: {
          ...this.member.extra_data,
          new_checkout: false
        }
      }
      this.$axios.patch(`/members/${this.member.id}/`, params)
        .then(resp => {
          this.$store.commit('user/setMember', resp.data)
          this.showAlert = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    clearTimer() {
      clearInterval(this.timer)
    },
  },
}
</script>

<style lang="scss" scoped>
.parent-scroll {
  overflow: hidden;
  height: var(--page-height) !important;
}
.child-scroll {
  overflow-y: scroll;
  height: var(--page-height) !important;
  margin-right: var(--nve-right-margin);
  padding-right: 10px;
}
</style>